import React, { useEffect, useState } from 'react';
import { getDoctors, createSlot, getAvailableSlots, getAllSlotsByDate } from '../api';
import { formatTime, formatTimeRange } from '../utils/format';
import styles from './Page.module.css';

function generateSlots(startTime, endTime, intervalMins) {
  const slots = [];
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  let current = sh * 60 + sm;
  const end = eh * 60 + em;
  while (current + intervalMins <= end) {
    const from = `${String(Math.floor(current / 60)).padStart(2, '0')}:${String(current % 60).padStart(2, '0')}`;
    current += intervalMins;
    const to = `${String(Math.floor(current / 60)).padStart(2, '0')}:${String(current % 60).padStart(2, '0')}`;
    slots.push({ startTime: from, endTime: to });
  }
  return slots;
}

function getDatesInRange(from, to) {
  const dates = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date();
  maxDate.setDate(today.getDate() + 60);
  for (
    let d = new Date(from);
    d <= new Date(to) && d <= maxDate;
    d.setDate(d.getDate() + 1)
  ) {
    if (d.getDay() !== 0)
      dates.push(new Date(d).toISOString().split('T')[0]);
  }
  return dates;
}

export default function SlotsPage() {
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sessions, setSessions] = useState([
    { startTime: '09:00', endTime: '13:00', interval: '30' },
  ]);
  const [breakStart, setBreakStart] = useState('13:00');
  const [breakEnd, setBreakEnd] = useState('14:00');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const [checkDoctorId, setCheckDoctorId] = useState('');
  const [checkDate, setCheckDate] = useState('');
  const [slotStats, setSlotStats] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 60);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  useEffect(() => { getDoctors().then(r => setDoctors(r.data)); }, []);

  const handleSessionChange = (index, field, value) => {
    const updated = [...sessions];
    updated[index][field] = value;
    setSessions(updated);
  };

  const addSession = () => {
    if (sessions.length >= 2) return;
    setSessions([...sessions, { startTime: '14:00', endTime: '18:00', interval: '30' }]);
  };

  const removeSession = (index) => {
    setSessions(sessions.filter((_, i) => i !== index));
  };

  const handlePreview = (e) => {
    e.preventDefault();
    if (!doctorId || !fromDate || !toDate) return;
    const allSlots = sessions.flatMap(s =>
      s.startTime && s.endTime
        ? generateSlots(s.startTime, s.endTime, parseInt(s.interval))
        : []
    );
    const dates = getDatesInRange(fromDate, toDate);
    setPreview({ slots: allSlots, dates, total: allSlots.length * dates.length });
    setMsg('');
  };

  const handleGenerate = async () => {
    if (!preview) return;
    setLoading(true);
    setMsg('');
    let created = 0;

    try {
      for (const date of preview.dates) {
        let existingSlots = [];
        try {
          const res = await getAllSlotsByDate(parseInt(doctorId), date);
          existingSlots = res.data;
        } catch { existingSlots = []; }

        if (existingSlots.length > 0) {
          const doctor = doctors.find(d => d.id === parseInt(doctorId));
          setMsg('Slots already exist for Dr. ' + doctor.name + ' on ' + date + '. Please delete existing slots before regenerating.');
          setLoading(false);
          return;
        }

        for (const slot of preview.slots) {
          await createSlot({
            doctor: { id: parseInt(doctorId) },
            date,
            startTime: slot.startTime,
            endTime: slot.endTime,
            available: true,
          });
          created++;
        }
      }
      setMsg('Created ' + created + ' slots successfully.');
      setPreview(null);
    } catch {
      setMsg('Failed to create some slots.');
    }
    setLoading(false);
  };

  const handleCheckSlots = async (e) => {
    e.preventDefault();
    if (!checkDoctorId || !checkDate) return;
    setCheckLoading(true);
    setSlotStats(null);
    try {
      const [availableRes, allRes] = await Promise.all([
        getAvailableSlots(parseInt(checkDoctorId), checkDate),
        getAllSlotsByDate(parseInt(checkDoctorId), checkDate),
      ]);
      const available = availableRes.data;
      const all = allRes.data;
      const booked = all.filter(s => !s.available);
      setSlotStats({
        available: available.length,
        booked: booked.length,
        total: all.length,
        slots: all,
      });
    } catch {
      setSlotStats({ error: 'No slots found for this date.' });
    }
    setCheckLoading(false);
  };

  return (
    <div>
      <h2 className={styles.heading}>Manage Appointment Slots</h2>
      <div className={styles.grid}>

        {/* LEFT — Generate Slots */}
        <div className={styles.card}>
          <h3>Generate Slots</h3>
          <form onSubmit={handlePreview}>
            <div className={styles.field}>
              <label>Doctor *</label>
              <select value={doctorId}
                onChange={e => { setDoctorId(e.target.value); setMsg(''); setPreview(null); }}
                required>
                <option value="">Select Doctor</option>
                {doctors.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name} - {d.specialization}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label>From Date *</label>
                <input type="date" value={fromDate}
                  min={today} max={maxDateStr}
                  onChange={e => setFromDate(e.target.value)} required />
              </div>
              <div className={styles.field}>
                <label>To Date *</label>
                <input type="date" value={toDate}
                  min={fromDate || today} max={maxDateStr}
                  onChange={e => setToDate(e.target.value)} required />
              </div>
            </div>

            {sessions.map((s, i) => (
              <div key={i} className={styles.sessionBox}>
                <div className={styles.sessionHeader}>
                  <strong>Session {i + 1}</strong>
                  {sessions.length > 1 && (
                    <button type="button" className={styles.removeBtn}
                      onClick={() => removeSession(i)}>
                      Remove
                    </button>
                  )}
                </div>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label>Start Time</label>
                    <input type="time" value={s.startTime}
                      onChange={e => handleSessionChange(i, 'startTime', e.target.value)}
                      required />
                  </div>
                  <div className={styles.field}>
                    <label>End Time</label>
                    <input type="time" value={s.endTime}
                      onChange={e => handleSessionChange(i, 'endTime', e.target.value)}
                      required />
                  </div>
                </div>
                <div className={styles.field}>
                  <label>Slot Interval</label>
                  <select value={s.interval}
                    onChange={e => handleSessionChange(i, 'interval', e.target.value)}>
                    <option value="15">15 minutes</option>
                    <option value="20">20 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">60 minutes</option>
                  </select>
                </div>
              </div>
            ))}

            {sessions.length === 2 && (
              <div className={styles.breakBox}>
                <strong>Break / Lunch Time</strong>
                <div className={styles.row} style={{ marginTop: 8 }}>
                  <div className={styles.field}>
                    <label>Break Start</label>
                    <input type="time" value={breakStart}
                      onChange={e => setBreakStart(e.target.value)} />
                  </div>
                  <div className={styles.field}>
                    <label>Break End</label>
                    <input type="time" value={breakEnd}
                      onChange={e => setBreakEnd(e.target.value)} />
                  </div>
                </div>
              </div>
            )}

            {sessions.length < 2 && (
              <button type="button" className={styles.btnOutline}
                style={{ marginBottom: 12 }} onClick={addSession}>
                + Add Second Session
              </button>
            )}

            <button type="submit" className={styles.btn}>Preview Slots</button>
          </form>

          {preview && (
            <div className={styles.preview}>
              <p>
                <strong>{preview.dates.length} days</strong> x{' '}
                <strong>{preview.slots.length} slots/day</strong> ={' '}
                <strong>{preview.total} total slots</strong>
              </p>
              <div className={styles.slotGrid}>
                {preview.slots.map((s, i) => (
                  <span key={i} className={styles.slotChip}>
                    {formatTimeRange(s.startTime, s.endTime)}
                  </span>
                ))}
              </div>
              {msg && (
                <p style={{ color: '#b71c1c', fontWeight: 500, marginTop: 10, fontSize: 14 }}>
                  {msg}
                </p>
              )}
              {!msg && (
                <button className={styles.btn} onClick={handleGenerate}
                  disabled={loading} style={{ marginTop: 12 }}>
                  {loading ? 'Creating slots...' : 'Confirm & Create ' + preview.total + ' Slots'}
                </button>
              )}
            </div>
          )}
          {msg && !preview && (
            <p style={{ color: '#b71c1c', fontWeight: 500, marginTop: 12, fontSize: 14 }}>
              {msg}
            </p>
          )}
        </div>

        {/* RIGHT — Check Slot Status */}
        <div className={styles.card}>
          <h3>Check Slot Status for a Date</h3>
          <form onSubmit={handleCheckSlots}>
            <div className={styles.field}>
              <label>Doctor *</label>
              <select value={checkDoctorId}
                onChange={e => setCheckDoctorId(e.target.value)} required>
                <option value="">Select Doctor</option>
                {doctors.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name} - {d.specialization}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label>Date *</label>
              <input type="date" value={checkDate}
                onChange={e => setCheckDate(e.target.value)} required />
            </div>
            <button type="submit" className={styles.btn} disabled={checkLoading}>
              {checkLoading ? 'Checking...' : 'Check Slots'}
            </button>
          </form>

          {slotStats && !slotStats.error && (
            <div style={{ marginTop: 20 }}>
              <div className={styles.statsRow}>
                <div className={styles.statCard}>
                  <strong style={{ color: '#2e7d32' }}>{slotStats.available}</strong>
                  <span>Available</span>
                </div>
                <div className={styles.statCard}>
                  <strong style={{ color: '#c0272d' }}>{slotStats.booked}</strong>
                  <span>Booked</span>
                </div>
                <div className={styles.statCard}>
                  <strong style={{ color: '#1a3a6b' }}>{slotStats.total}</strong>
                  <span>Total</span>
                </div>
              </div>
              <div className={styles.slotGrid} style={{ marginTop: 12 }}>
                {slotStats.slots.map((s, i) => (
                  <span key={i} className={styles.slotChip}
                    style={{
                      background: s.available ? '#e8f5e9' : '#fce4e4',
                      color: s.available ? '#2e7d32' : '#c0272d',
                    }}>
                    {formatTime(s.startTime)} {s.available ? '✓' : '✗'}
                  </span>
                ))}
              </div>
            </div>
          )}
          {slotStats && slotStats.error && (
            <p style={{ marginTop: 16, color: '#b71c1c', fontSize: 14 }}>
              {slotStats.error}
            </p>
          )}
        </div>

      </div>
    </div>
  );
}
